/**
 * Authentication API Client
 * Backend API endpoints for authentication and user management
 */

import { STORAGE_KEYS } from './constants';
import type { ScheduleStatus } from './api-types';
import { logger } from './logger';
import type {
    ForceStopResponse,
    LiveStreamSummary,
    MediaKind,
    MediaReviewItem,
    ModerationActionListResponse,
    ModerationReasonRequest,
    MonthlyReportKind,
    MyModerationStatus,
    NgDetectionListResponse,
    NgWord,
    NgWordAction,
    StreamerContent,
    StreamerModerationState,
    SuspensionResponse,
} from './types';

// Re-export ScheduleStatus for convenience
export type { ScheduleStatus } from './api-types';

// Use relative path to leverage Vite proxy in development
// In production, this should be configured via reverse proxy
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || '';
const REFRESH_COOKIE_SESSION_MARKER = '__refresh_cookie__';

export interface LoginRequest {
    username: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
    refresh_token?: string;
    token_type: string;
    user?: {
        id: number;
        username: string;
        role: string;
    };
    must_change_password?: boolean;
}

export interface RefreshRequest {
    refresh_token?: string;
}

export interface User {
    id: number;
    username: string;
    role: 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER';
    created_at: string;
    affiliation_id?: number | null;
    affiliation_name?: string | null;
    // eKYC関連（配信者のみ）
    is_verified?: boolean | null;
    ekyc_status?: EKYCStatus | null;
}

export interface CreateUserRequest {
    username: string;
    role: 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER';
    affiliation_id?: number;
}

export interface CreateUserResponse extends User {
    password: string;
    message: string;
}

export interface UpdateUserRequest {
    username?: string;
    role?: 'ADMIN' | 'MANAGER' | 'STREAMER' | 'VIEWER';
    password?: string;
    affiliation_id?: number;
}

// Affiliation interfaces
export interface Affiliation {
    id: number;
    name: string;
    description: string | null;
    created_at: string;
    user_count: number;
}

export interface CreateAffiliationRequest {
    name: string;
    description?: string;
}

export interface UpdateAffiliationRequest {
    name?: string;
    description?: string;
}

export interface PasswordChangeRequest {
    old_password: string;
    new_password: string;
}

export interface Viewer {
    id: number;
    username: string;
    created_at: string;
    points: number;
    handle_name?: string | null;
}

export type ReportReason = 'suspected_minor' | 'prohibited_content' | 'rights_violation' | 'other';
export type ReportStatus = 'received' | 'investigating' | 'resolved' | 'dismissed';
export type ReportSubjectType = 'live_room' | 'profile' | 'chat' | 'other';
export type ComplaintChannel = 'in_app' | 'email' | 'postal' | 'phone' | 'other';
export type ComplainantType = 'user' | 'performer' | 'advocacy_org' | 'other';
export type ReportDueState = 'closed' | 'overdue' | 'due_soon' | 'on_track';
export interface ContentReport {
    id: number; reporter_user_id: number | null; reporter_username: string | null;
    subject_type: ReportSubjectType; streamer_id: number | null; streamer_username: string | null; stream_key: string | null;
    reason: ReportReason; details: string; status: ReportStatus; handled_by: number | null;
    resolution_note: string | null; created_at: string; updated_at: string;
    channel: ComplaintChannel; complainant_type: ComplainantType; complainant_name: string | null;
    received_at: string; due_at: string; due_state: ReportDueState; business_days_remaining: number;
    resolved_at: string | null; acquirer_reported_at: string | null; created_by: number | null;
}
export interface ContentReportListResponse { reports: ContentReport[]; total: number; limit: number; offset: number; }
export interface ProfileImageUploadResponse {
    message: string; profile_image_url: string | null; status: 'pending'; submission_id: number;
}
export interface ContentReportFilters { status?: ReportStatus; openOnly?: boolean; overdueOnly?: boolean; channel?: ComplaintChannel; }
export interface ExternalComplaintCreate {
    received_at: string; channel: Exclude<ComplaintChannel, 'in_app'>; complainant_type: ComplainantType;
    complainant_name?: string | null; streamer_username?: string | null; subject_type: ReportSubjectType;
    stream_key?: string | null; reason: ReportReason; details: string;
}

// Pagination response interfaces
export interface UserListResponse {
    users: User[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
}

export interface ViewerListResponse {
    viewers: Viewer[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
}

// Filter parameters interfaces
export interface UserListParams {
    page?: number;
    limit?: number;
    username?: string;
    role?: 'ADMIN' | 'MANAGER' | 'STREAMER';
    affiliation_id?: number;
    created_from?: string;
    created_to?: string;
}

export interface ViewerListParams {
    page?: number;
    limit?: number;
    username?: string;
    created_from?: string;
    created_to?: string;
}

// Bulk notification interfaces
export interface BulkNotificationCreate {
    recipient_ids: number[];
    recipient_type: 'viewers' | 'streamers';
    title: string;
    message: string;
    link_url?: string;
    image_url?: string;
}

export interface BulkNotificationResponse {
    sent_count: number;
    failed_count: number;
    total_requested: number;
}

export interface ViewerProfile {
    id: number;
    username: string;
    created_at: string;
    points: number;
    handle_name: string | null;
}

export interface HandleNameResponse {
    handle_name: string | null;
    display_name: string;
}

export interface LanguageResponse {
    language: string;
}

export interface CreateViewerRequest {
    username: string;
}

// Viewer authentication interfaces (email + password)
export interface ViewerSignupRequest {
    email: string;
    username: string;
    password: string;
}

export interface ViewerSignupResponse {
    message: string;
    email: string;
    requires_verification: boolean;
}

export interface ViewerFullSignupRequest {
    email: string;
    password: string;
    full_name: string;
    full_name_kana?: string;
    postal_code?: string;
    prefecture?: string;
    city?: string;
    address?: string;
    building?: string;
    phone?: string;
    company_name?: string;
    birth_date: string;
    gender?: string;
    occupation?: string;
    terms_agreed: boolean;
}

export interface ViewerEmailLoginRequest {
    email: string;
    password: string;
}

export interface VerifyEmailRequest {
    token: string;
}

export interface VerifyEmailResponse {
    message: string;
    verified: boolean;
    access_token?: string;
    refresh_token?: string;
    session_id?: string;
    user?: {
        id: number;
        username: string;
        role: string;
    };
}

export interface ResendVerificationRequest {
    email: string;
}

export interface ResendVerificationResponse {
    message: string;
}

export interface ForgotPasswordRequest {
    email: string;
}

export interface ForgotPasswordResponse {
    message: string;
}

export interface ResetPasswordRequest {
    token: string;
    new_password: string;
}

export interface ResetPasswordResponse {
    message: string;
}

// Event Schedule interfaces
// Note: ScheduleStatus is imported from api-types.ts (generated from OpenAPI)
export interface EventSchedule {
    id: string;  // UUID string
    streamer_id: number;
    scheduled_start: string;
    scheduled_end: string;
    status: ScheduleStatus;
    created_by: number | null;
    created_at: string;
    updated_at: string;
    streamer_username: string | null;
    streamer_display_name: string | null;
    streamer_profile_image_url: string | null;
    is_live?: boolean;
}

export interface EventScheduleListResponse {
    schedules: EventSchedule[];
    total: number;
}

export interface EventScheduleCreate {
    streamer_id: number;
    scheduled_start: string;
    scheduled_end: string;
}

export interface EventScheduleUpdate {
    scheduled_start?: string;
    scheduled_end?: string;
    status?: ScheduleStatus;
}

// Favorites interfaces
export interface FavoriteStreamer {
    id: number;
    streamer_id: number;
    streamer_username: string;
    streamer_display_name: string | null;
    streamer_profile_image_url: string | null;
    is_online: boolean;
    viewer_count: number | null;
    created_at: string;
}

export interface FavoriteListResponse {
    favorites: FavoriteStreamer[];
    total: number;
}

export interface FavoriteStatusResponse {
    is_favorite: boolean;
}

export interface FavoriteScheduleItem {
    schedule_id: string;  // UUID string
    streamer_id: number;
    streamer_username: string;
    streamer_display_name: string | null;
    streamer_profile_image_url: string | null;
    scheduled_start: string;
    scheduled_end: string;
    status: string;  // scheduled, live, ended, cancelled
    is_live: boolean;
}

export interface FavoriteScheduleListResponse {
    schedules: FavoriteScheduleItem[];
    total: number;
}

// Event Notification Subscription interfaces
export interface EventNotificationSubscription {
    id: number;
    schedule_id: string;
    created_at: string;
}

export interface EventNotificationStatus {
    subscribed: boolean;
    subscription_id?: number;
    notification_sent: boolean;
}

export interface EventNotificationWithEvent {
    id: number;
    schedule_id: string;
    event_title: string | null;
    streamer_name: string;
    scheduled_start: string;
    created_at: string;
    notified_at: string | null;
}

// Site settings interfaces
export interface ScheduleNoticePrefix {
    value: string;
    source: 'global' | 'affiliation' | 'default';
    affiliation_id: number | null;
    updated_at: string | null;
}

// STT (Speech-to-Text) settings interfaces
export type STTProvider = 'deepgram' | 'gladia';

export interface STTSettingsResponse {
    provider: STTProvider;
    enabled: boolean;
    available_providers: STTProvider[];
    updated_at: string | null;
}

export interface STTSettingsUpdate {
    provider?: STTProvider;
    enabled?: boolean;
}

// Translation settings interfaces
export type TranslationProvider = 'deepl' | 'openai';

export interface TranslationSettingsResponse {
    provider: TranslationProvider;
    enabled: boolean;
    available_providers: TranslationProvider[];
    updated_at?: string | null;
}

export interface TranslationSettingsUpdate {
    provider?: TranslationProvider;
    enabled?: boolean;
}

// Banner interfaces
export interface Banner {
    id: number;
    location: string;
    image_path: string | null;
    alt_text: string;
    link_url: string | null;
    is_active: boolean;
    display_order: number;
    created_at: string;
    updated_at: string;
}

export interface BannerListResponse {
    banners: Banner[];
}

export interface BannerUpdateRequest {
    image_path?: string;
    alt_text?: string;
    link_url?: string | null;
    is_active?: boolean;
    display_order?: number;
}

export interface BannerUploadResponse {
    image_path: string;
    message: string;
}

// Notification interfaces
export interface Notification {
    id: number;
    sender_id: number;
    sender_type: 'admin' | 'manager' | 'streamer';
    sender_name: string | null;
    recipient_type: 'all' | 'individual';
    recipient_id: number | null;
    title: string;
    message: string;
    link_url: string | null;
    image_url: string | null;
    created_at: string;
    is_read: boolean;
}

export interface NotificationListResponse {
    notifications: Notification[];
    unread_count: number;
}

export interface NotificationCreate {
    recipient_type: 'all' | 'individual';
    recipient_id?: number;
    title: string;
    message: string;
    link_url?: string;
    image_url?: string;
}

// Sent notification interfaces (for admin/manager)
export interface SentNotification {
    id: number;
    sender_id: number;
    sender_type: 'admin' | 'manager' | 'streamer';
    sender_name: string | null;
    recipient_type: 'all' | 'individual';
    recipient_id: number | null;
    recipient_name: string | null;
    title: string;
    message: string;
    link_url: string | null;
    image_url: string | null;
    created_at: string;
    read_count: number;
}

export interface SentNotificationListResponse {
    notifications: SentNotification[];
    total_count: number;
}

export interface StreamStartRequest {
    stream_key: string;
    username: string;
    user_id: number;
    schedule_id?: string;  // Event schedule ID (UUID) for this stream
}

export interface StreamStopRequest {
    stream_key: string;
}

export interface ActiveStream {
    stream_key: string;
    username: string;
    user_id: number;
    started_at: string;
    status: 'waiting' | 'party' | 'two_shot' | 'paused';
    broadcast_status: 'live' | 'paused' | 'suspended';
    schedule_id?: string;  // Event schedule ID (UUID) for this stream
}

export interface ActiveStreamsResponse {
    streams: ActiveStream[];
    count: number;
}

export interface StreamInfoResponse {
    stream: ActiveStream;
}

export interface ChatMessage {
    id: number;
    stream_key: string;
    user_id: number;
    username: string;
    message: string;
    is_streamer: boolean;
    created_at: string;
    schedule_id?: string;  // Event schedule ID (UUID) for filtering chat by schedule
    original_lang?: string;  // Language of the original message ('ja' | 'en')
    display_text?: string | null;  // Translated text if available, otherwise null
}

export interface ChatMessageCreate {
    message: string;
    schedule_id?: string;  // Event schedule ID (UUID) for filtering chat by schedule
    original_lang?: string;  // Language of the original message ('ja' | 'en')
}

export interface StreamViewer {
    stream_key: string;
    user_id: number;
    username: string;
    last_seen: string;
    user_status?: string; // before_entry, party, peek, two_shot
}

export interface StreamViewerListResponse {
    stream_key: string;
    count: number;
    viewers: StreamViewer[];
}

export interface PointPurchaseRequest {
    amount: 100 | 300 | 500 | 1000 | 3000 | 5000 | 10000 | 30000 | 50000;
}

export interface PointBalanceResponse {
    points: number;
}

export interface PointConsumptionResponse {
    points: number;
    consumed: number;
}

// Secret message interfaces
export interface SecretMessageTemplate {
    id: number;
    text: string;
}

export interface SecretMessage {
    id: number;
    stream_key: string;
    user_id: number;
    user_username: string;
    template_id: number;
    message_text: string;
    created_at: string;
    schedule_id?: string;  // Event schedule ID (UUID) for filtering by schedule
    original_lang?: string;  // Language of the original message ('ja' | 'en')
    display_text?: string | null;  // Translated text if available, otherwise null
}

export interface SecretMessageCreate {
    stream_key: string;
    template_id: number;
    schedule_id?: string;  // Event schedule ID (UUID) for filtering by schedule
    original_lang?: string;  // Language of the original message ('ja' | 'en')
}

// eKYC Types
export type EKYCStatus = 'NOT_APPLIED' | 'SUBMITTED' | 'API_VERIFYING' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

// 配信者向けAPI
export interface EKYCApplicationResponse {
    id: number;
    streamer_id: number;
    status: EKYCStatus;
    rejection_reason?: string;
    created_at: string;
    reviewed_at?: string;
    provider_redirect_url?: string;
    id_document_url?: string;
    selfie_url?: string;
    api_verification_result?: Record<string, unknown>;
    api_verified_at?: string;
}

export interface EKYCStatusResponse {
    streamer_id: number;
    is_verified: boolean;
    status?: EKYCStatus;
    verified_at?: string;
    can_apply: boolean;
    rejection_reason?: string;
}

// 管理者向けAPI
export interface EKYCApplicationListItem {
    id: number;
    streamer_id: number;
    username: string;
    display_name?: string;
    status: EKYCStatus;
    created_at: string;
    reviewed_at?: string;
    reviewed_by_username?: string;
    api_verified_at?: string;
    rejection_reason?: string;
}

export interface EKYCApplicationListResponse {
    items: EKYCApplicationListItem[];
    total: number;
    page: number;
    per_page: number;
}

export interface EKYCReviewRequest {
    approved: boolean;
    rejection_reason?: string;
}

export interface EKYCReviewResponse {
    id: number;
    status: EKYCStatus;
    reviewed_at: string;
    message: string;
}

// Backup interfaces
export interface BackupInfo {
    filename: string;
    size: number;
    size_human: string;
    created_at: string;
    backup_type: 'auto' | 'manual' | 'pre_test' | 'before_restore' | string;
    compressed: boolean;
}

export interface BackupListResponse {
    backups: BackupInfo[];
    total_count: number;
    total_size: number;
    total_size_human: string;
    backup_dir: string;
    // Pagination fields
    page: number;
    page_size: number;
    total_pages: number;
}

export interface BackupCreateResponse {
    success: boolean;
    filename: string;
    size: number;
    size_human: string;
    message: string;
}

export interface BackupDeleteResponse {
    success: boolean;
    filename: string;
    message: string;
}

export interface FileChecks {
    exists: boolean;
    size: number;
    gzip_valid: boolean | null;  // null for non-gzip files (.sql)
    sql_header_valid: boolean;
}

export interface RestoreTestResult {
    success: boolean;
    message: string;
    table_counts: Record<string, number> | null;
}

export interface BackupVerifyResponse {
    filename: string;
    file_size: number;
    file_size_human: string;
    file_checks: FileChecks;
    is_valid: boolean;
    verified_at: string;
    restore_test: RestoreTestResult | null;
}

class ApiError extends Error {
    constructor(
        public status: number,
        public detail: string,
        public code?: string,
        public params?: Record<string, unknown>,
        public validation = false,
    ) {
        super(detail);
        this.name = 'ApiError';
    }
}

// セッション期限切れ専用のエラークラス
class SessionExpiredError extends Error {
    constructor() {
        super('セッションが切れました。再ログインしてください。');
        this.name = 'SessionExpiredError';
    }
}

// セッション期限切れ時のリダイレクト処理
function handleSessionExpired() {
    // ブラウザ環境でのみ実行
    if (typeof window !== 'undefined') {
        // セッション無効化時は元のページに戻さない
        // （別デバイスログインによる強制ログアウトの場合、視聴中のルームが既に終了している可能性があるため）
        sessionStorage.removeItem('redirectAfterLogin');
        // ログインページへリダイレクト
        window.location.href = '/login';
    }
}

class AuthApiClient {
    private accessToken: string | null = null;
    private refreshToken: string | null = null;
    private refreshPromise: Promise<boolean> | null = null;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;
    private periodicCheckTimer: ReturnType<typeof setInterval> | null = null;
    // トークン期限切れの何分前にリフレッシュするか（環境変数で設定可能、デフォルト20分）
    private static readonly REFRESH_MARGIN_MS =
        (parseInt(import.meta.env.VITE_TOKEN_REFRESH_MARGIN_MINUTES || '20', 10)) * 60 * 1000;
    // 定期チェック間隔（環境変数で設定可能、デフォルト2分）
    private static readonly PERIODIC_CHECK_INTERVAL_MS =
        (parseInt(import.meta.env.VITE_TOKEN_PERIODIC_CHECK_MINUTES || '2', 10)) * 60 * 1000;

    constructor() {
        // Load tokens from localStorage on initialization
        this.loadTokens();
        // プロアクティブリフレッシュを開始
        this.scheduleProactiveRefresh();
        // 定期チェックを開始（setTimeoutのバックアップ）
        this.startPeriodicCheck();
        // Page Visibility APIでタブがアクティブになった時にチェック
        this.setupVisibilityChangeHandler();
    }

    /**
     * タブがフォアグラウンドに戻った時にトークンをチェック
     * ブラウザのタイマースロットリングでリフレッシュが遅延した場合の回復策
     */
    private setupVisibilityChangeHandler() {
        if (typeof document === 'undefined') return;

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                logger.info('[AuthApiClient] タブがアクティブになりました、トークンをチェック');
                // トークンが期限切れまたは期限切れ間近ならリフレッシュ
                if (this.accessToken && this.refreshToken) {
                    const payload = this.parseJwt(this.accessToken);
                    if (payload?.exp) {
                        const expiresAt = payload.exp * 1000;
                        const now = Date.now();
                        const timeUntilExpiry = expiresAt - now;

                        if (timeUntilExpiry <= 0) {
                            // すでに期限切れ - リフレッシュトークンで回復を試みる
                            logger.info('[AuthApiClient] トークン期限切れを検出、リフレッシュ試行');
                            this.refresh();
                        } else if (timeUntilExpiry <= AuthApiClient.REFRESH_MARGIN_MS) {
                            // 期限切れ間近
                            logger.info('[AuthApiClient] トークン期限切れ間近を検出、リフレッシュ実行');
                            this.refresh();
                        }
                    }
                }
            }
        });
        logger.info('[AuthApiClient] Page Visibility監視を開始');
    }

    private getLocalStorage(): Storage | null {
        return typeof localStorage === 'undefined' ? null : localStorage;
    }

    private getSessionStorage(): Storage | null {
        return typeof sessionStorage === 'undefined' ? null : sessionStorage;
    }

    private loadTokens() {
        const local = this.getLocalStorage();
        const session = this.getSessionStorage();

        this.accessToken = null;
        this.refreshToken = null;

        // Tokens are kept in memory only. Clear legacy persisted values left by
        // older frontend versions so they are not exposed through storage.
        local?.removeItem?.(STORAGE_KEYS.ACCESS_TOKEN);
        local?.removeItem?.(STORAGE_KEYS.REFRESH_TOKEN);
        session?.removeItem?.(STORAGE_KEYS.REFRESH_TOKEN);
    }

    private saveTokens(accessToken: string, _refreshToken?: string) {
        const local = this.getLocalStorage();
        const session = this.getSessionStorage();

        this.accessToken = accessToken;
        this.refreshToken = REFRESH_COOKIE_SESSION_MARKER;
        local?.removeItem?.(STORAGE_KEYS.ACCESS_TOKEN);
        local?.removeItem?.(STORAGE_KEYS.REFRESH_TOKEN);
        session?.removeItem?.(STORAGE_KEYS.REFRESH_TOKEN);
        // 新しいトークンでプロアクティブリフレッシュをスケジュール
        this.scheduleProactiveRefresh();
    }

    private clearTokens() {
        const local = this.getLocalStorage();
        const session = this.getSessionStorage();

        this.accessToken = null;
        this.refreshToken = null;
        local?.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        local?.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        session?.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        // リフレッシュタイマーをクリア
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        // 定期チェックタイマーをクリア
        this.stopPeriodicCheck();
    }

    /**
     * JWTトークンからペイロードを取得
     */
    private parseJwt(token: string): { exp?: number } | null {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch {
            return null;
        }
    }

    /**
     * トークン期限切れ前にプロアクティブにリフレッシュをスケジュール
     */
    private scheduleProactiveRefresh() {
        // 既存のタイマーをクリア
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        if (!this.accessToken || !this.refreshToken) {
            return;
        }

        const payload = this.parseJwt(this.accessToken);
        if (!payload?.exp) {
            return;
        }

        const expiresAt = payload.exp * 1000; // ミリ秒に変換
        const now = Date.now();
        const timeUntilRefresh = expiresAt - now - AuthApiClient.REFRESH_MARGIN_MS;

        if (timeUntilRefresh <= 0) {
            // すでに期限切れ間近または期限切れ - 即座にリフレッシュ
            logger.info('[AuthApiClient] トークン期限切れ間近、即座にリフレッシュ実行');
            this.refresh();
        } else {
            // 期限切れ10分前にリフレッシュをスケジュール
            logger.info(`[AuthApiClient] ${Math.round(timeUntilRefresh / 1000 / 60)}分後にトークンリフレッシュをスケジュール`);
            this.refreshTimer = setTimeout(() => {
                logger.info('[AuthApiClient] プロアクティブトークンリフレッシュ実行');
                this.refresh();
            }, timeUntilRefresh);
        }
    }

    /**
     * 定期的にトークンの有効期限をチェック（setTimeoutが失敗した場合のバックアップ）
     * ブラウザのタイマースロットリングにより、バックグラウンドタブではsetTimeoutが
     * 大幅に遅延する可能性があるため、setIntervalで定期的にチェックする
     */
    private startPeriodicCheck() {
        // 既存のタイマーをクリア
        this.stopPeriodicCheck();

        this.periodicCheckTimer = setInterval(() => {
            if (this.isTokenExpiringSoon()) {
                logger.info('[AuthApiClient] 定期チェック: トークン期限切れ間近を検出、リフレッシュ実行');
                this.refresh();
            }
        }, AuthApiClient.PERIODIC_CHECK_INTERVAL_MS);

        logger.info('[AuthApiClient] 定期トークンチェック開始（2分間隔）');
    }

    /**
     * 定期チェックを停止
     */
    private stopPeriodicCheck() {
        if (this.periodicCheckTimer) {
            clearInterval(this.periodicCheckTimer);
            this.periodicCheckTimer = null;
        }
    }

    /**
     * トークンが期限切れ間近かどうかをチェック
     * @returns トークンがマージン内で期限切れになる場合はtrue
     */
    private isTokenExpiringSoon(): boolean {
        if (!this.accessToken || !this.refreshToken) {
            return false;
        }

        const payload = this.parseJwt(this.accessToken);
        if (!payload?.exp) {
            return false;
        }

        const expiresAt = payload.exp * 1000;
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        // マージン内に期限切れになる場合はtrue
        return timeUntilExpiry <= AuthApiClient.REFRESH_MARGIN_MS;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        // リクエスト前チェック: トークンが期限切れ間近なら先にリフレッシュ
        // 認証関連エンドポイントはスキップ（リフレッシュ自体が401を返す可能性があるため）
        if (!endpoint.includes('/auth/') && this.isTokenExpiringSoon()) {
            logger.info('[AuthApiClient] リクエスト前チェック: トークン期限切れ間近を検出、先にリフレッシュ実行');
            await this.refresh();
        }

        const url = `${API_BASE_URL}${endpoint}`;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(options.headers as Record<string, string>),
        };

        // Add auth header if access token exists
        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        try {
            const response = await fetch(url, {
                ...options,
                credentials: 'include',
                headers,
            });

            // Handle 401 - try to refresh token once
            if (response.status === 401 && this.refreshToken && !endpoint.includes('/auth/refresh')) {
                const apiError = await this.toApiError(response);
                if (apiError.code) throw apiError;
                const refreshed = await this.refresh();
                if (refreshed) {
                    // Retry original request with new token
                    headers['Authorization'] = `Bearer ${this.accessToken}`;
                    const retryResponse = await fetch(url, {
                        ...options,
                        credentials: 'include',
                        headers,
                    });

                    // リトライ後も401の場合はセッション切れ（セッションがDB側で無効化されている）
                    if (retryResponse.status === 401) {
                        this.clearTokens();
                        handleSessionExpired();
                        throw new SessionExpiredError();
                    }

                    if (!retryResponse.ok) throw await this.toApiError(retryResponse);
                    return retryResponse.json();
                } else {
                    // リフレッシュトークンも期限切れ - セッション切れとして処理
                    handleSessionExpired();
                    throw new SessionExpiredError();
                }
            }

            // 401でリフレッシュトークンがない場合もセッション切れとして処理
            if (response.status === 401 && !endpoint.includes('/auth/')) {
                this.clearTokens();
                handleSessionExpired();
                throw new SessionExpiredError();
            }

            if (!response.ok) throw await this.toApiError(response);

            // Handle 204 No Content
            if (response.status === 204) {
                return {} as T;
            }

            return response.json();
        } catch (error) {
            if (error instanceof ApiError || error instanceof SessionExpiredError) {
                throw error;
            }
            throw new ApiError(0, error instanceof Error ? error.message : 'Network error');
        }
    }

    private async toApiError(response: Response): Promise<ApiError> {
        let error: { detail?: unknown; code?: unknown; params?: unknown } = {};
        try { error = await response.json(); } catch { return new ApiError(response.status, 'Request failed'); }
        const rawDetail = error.detail;
        const validation = Array.isArray(rawDetail);
        const detail = validation
            ? rawDetail.map((item) => typeof item === 'object' && item !== null && 'msg' in item && typeof item.msg === 'string' ? item.msg : '').filter(Boolean).join(', ')
            : typeof rawDetail === 'string' ? rawDetail : rawDetail ? JSON.stringify(rawDetail) : 'Request failed';
        const code = typeof error.code === 'string' ? error.code : undefined;
        const params = error.params && typeof error.params === 'object' && !Array.isArray(error.params)
            ? error.params as Record<string, unknown> : undefined;
        return new ApiError(response.status, detail, code, params, validation);
    }

    // Authentication endpoints

    async login(username: string, password: string): Promise<LoginResponse> {
        const response = await this.request<LoginResponse>('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });
        this.saveTokens(response.access_token, response.refresh_token);
        return response;
    }

    // Viewer authentication methods (email + password)

    async viewerFullSignup(data: ViewerFullSignupRequest): Promise<ViewerSignupResponse> {
        return this.request<ViewerSignupResponse>('/api/auth/viewer/full-signup', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async viewerEmailLogin(email: string, password: string): Promise<LoginResponse & { user: { id: number; username: string; role: string } }> {
        const response = await this.request<LoginResponse & { user: { id: number; username: string; role: string } }>('/api/auth/viewer/email-login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        this.saveTokens(response.access_token, response.refresh_token);
        return response;
    }

    async verifyEmail(token: string): Promise<VerifyEmailResponse> {
        const response = await this.request<VerifyEmailResponse>('/api/auth/viewer/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
        // 確認成功時はトークンを保存
        if (response.access_token) {
            this.saveTokens(response.access_token, response.refresh_token);
        }
        return response;
    }

    async resendVerification(email: string): Promise<ResendVerificationResponse> {
        return this.request<ResendVerificationResponse>('/api/auth/viewer/resend-verification', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
        return this.request<ForgotPasswordResponse>('/api/auth/viewer/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async resetPassword(token: string, newPassword: string): Promise<ResetPasswordResponse> {
        return this.request<ResetPasswordResponse>('/api/auth/viewer/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, new_password: newPassword }),
        });
    }

    async refresh(): Promise<boolean> {
        // 既にリフレッシュ中の場合は、その結果を待つ（同時リフレッシュ防止）
        if (this.refreshPromise) {
            logger.info('[AuthApiClient] 既存のリフレッシュ処理を待機中...');
            return this.refreshPromise;
        }

        // リフレッシュ処理を開始
        this.refreshPromise = this.doRefresh();

        try {
            return await this.refreshPromise;
        } finally {
            this.refreshPromise = null;
        }
    }

    private async doRefresh(): Promise<boolean> {
        try {
            logger.info('[AuthApiClient] トークンリフレッシュ実行中...');
            const refreshOptions: RequestInit = { method: 'POST' };
            if (this.refreshToken && this.refreshToken !== REFRESH_COOKIE_SESSION_MARKER) {
                refreshOptions.body = JSON.stringify({ refresh_token: this.refreshToken });
            }
            const response = await this.request<LoginResponse>('/api/auth/refresh', refreshOptions);
            this.saveTokens(response.access_token, response.refresh_token);
            logger.info('[AuthApiClient] トークンリフレッシュ成功');
            return true;
        } catch (error) {
            logger.error(`[AuthApiClient] トークンリフレッシュ失敗: ${error}`);
            this.clearTokens();
            return false;
        }
    }

    async verify(): Promise<{ valid: boolean; user_id: string; username: string; role: string }> {
        if (!this.accessToken) {
            throw new ApiError(401, 'No access token');
        }
        return this.request(`/api/auth/verify`);
    }

    logout() {
        this.clearTokens();
    }

    isAuthenticated(): boolean {
        return this.accessToken !== null;
    }

    getAccessToken(): string | null {
        return this.accessToken;
    }

    // User management endpoints

    async listUsers(params?: UserListParams): Promise<UserListResponse> {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        if (params?.username) searchParams.append('username', params.username);
        if (params?.role) searchParams.append('role', params.role);
        if (params?.affiliation_id) searchParams.append('affiliation_id', params.affiliation_id.toString());
        if (params?.created_from) searchParams.append('created_from', params.created_from);
        if (params?.created_to) searchParams.append('created_to', params.created_to);
        const query = searchParams.toString();
        return this.request(`/api/users/${query ? '?' + query : ''}`);
    }

    async createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
        return this.request('/api/users/', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async getUser(userId: number): Promise<User> {
        return this.request(`/api/users/${userId}`);
    }

    async updateUser(userId: number, data: UpdateUserRequest): Promise<User> {
        return this.request(`/api/users/${userId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteUser(userId: number): Promise<void> {
        return this.request(`/api/users/${userId}`, {
            method: 'DELETE',
        });
    }

    async changePassword(data: PasswordChangeRequest): Promise<{ message: string }> {
        return this.request('/api/users/me/password', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Affiliation management endpoints (Admin only)

    async listAffiliations(): Promise<Affiliation[]> {
        return this.request('/api/affiliations/');
    }

    async createAffiliation(data: CreateAffiliationRequest): Promise<Affiliation> {
        return this.request('/api/affiliations/', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async getAffiliation(affiliationId: number): Promise<Affiliation> {
        return this.request(`/api/affiliations/${affiliationId}`);
    }

    async updateAffiliation(affiliationId: number, data: UpdateAffiliationRequest): Promise<Affiliation> {
        return this.request(`/api/affiliations/${affiliationId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteAffiliation(affiliationId: number): Promise<void> {
        return this.request(`/api/affiliations/${affiliationId}`, {
            method: 'DELETE',
        });
    }

    // Viewer management endpoints

    async listViewers(params?: ViewerListParams): Promise<ViewerListResponse> {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        if (params?.username) searchParams.append('username', params.username);
        if (params?.created_from) searchParams.append('created_from', params.created_from);
        if (params?.created_to) searchParams.append('created_to', params.created_to);
        const query = searchParams.toString();
        return this.request(`/api/viewers/${query ? '?' + query : ''}`);
    }

    async createViewer(data: CreateViewerRequest): Promise<Viewer> {
        return this.request('/api/viewers/', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async getViewer(viewerId: number): Promise<Viewer> {
        return this.request(`/api/viewers/${viewerId}`);
    }

    async deleteViewer(viewerId: number): Promise<void> {
        return this.request(`/api/viewers/${viewerId}`, {
            method: 'DELETE',
        });
    }

    // Event Schedule management endpoints

    async listEventSchedules(params?: {
        status_filter?: string;
        streamer_id?: number;
        upcoming_only?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<EventScheduleListResponse> {
        const searchParams = new URLSearchParams();
        if (params?.status_filter) searchParams.append('status_filter', params.status_filter);
        if (params?.streamer_id) searchParams.append('streamer_id', params.streamer_id.toString());
        if (params?.upcoming_only) searchParams.append('upcoming_only', 'true');
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        if (params?.offset) searchParams.append('offset', params.offset.toString());
        const query = searchParams.toString();
        return this.request(`/api/event-schedules/${query ? '?' + query : ''}`);
    }

    async getEventSchedule(scheduleId: number): Promise<EventSchedule> {
        return this.request(`/api/event-schedules/${scheduleId}`);
    }

    async createEventSchedule(data: EventScheduleCreate): Promise<EventSchedule> {
        return this.request('/api/event-schedules/', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateEventSchedule(scheduleId: string, data: EventScheduleUpdate): Promise<EventSchedule> {
        return this.request(`/api/event-schedules/${scheduleId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteEventSchedule(scheduleId: string): Promise<void> {
        return this.request(`/api/event-schedules/${scheduleId}`, {
            method: 'DELETE',
        });
    }

    // Stream management endpoints

    async startStream(data: StreamStartRequest): Promise<{ message: string; stream: ActiveStream }> {
        return this.request('/api/streams/start', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async authorizePublish(data: StreamStartRequest): Promise<{ message: string; stream_key: string; expires_at: string }> {
        return this.request('/api/streams/authorize-publish', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async stopStream(
        data: StreamStopRequest,
        options?: { keepalive?: boolean }
    ): Promise<{ message: string }> {
        return this.request('/api/streams/stop', {
            method: 'POST',
            body: JSON.stringify(data),
            keepalive: options?.keepalive ?? false,
        });
    }

    async getActiveStreams(): Promise<ActiveStreamsResponse> {
        return this.request('/api/streams/active');
    }

    async getStreamInfo(streamKey: string): Promise<StreamInfoResponse> {
        return this.request(`/api/streams/${streamKey}`);
    }

    async pauseStream(
        streamKey: string,
        options?: { keepalive?: boolean }
    ): Promise<{ message: string; stream: ActiveStream }> {
        return this.request(`/api/streams/${streamKey}/pause`, {
            method: 'POST',
            keepalive: options?.keepalive ?? false,
        });
    }

    async resumeStream(streamKey: string): Promise<{ message: string; stream: ActiveStream }> {
        return this.request(`/api/streams/${streamKey}/resume`, {
            method: 'POST',
        });
    }

    // Chat endpoints

    async getChatMessages(streamKey: string, limit: number = 50, scheduleId?: string, lang?: string): Promise<ChatMessage[]> {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (scheduleId !== undefined) {
            params.append('schedule_id', scheduleId);
        }
        if (lang !== undefined) {
            params.append('lang', lang);
        }
        return this.request(`/api/chat/${streamKey}/messages?${params.toString()}`);
    }

    async sendChatMessage(streamKey: string, message: string, scheduleId?: string, originalLang?: string): Promise<ChatMessage> {
        return this.request(`/api/chat/${streamKey}/messages`, {
            method: 'POST',
            body: JSON.stringify({ message, schedule_id: scheduleId, original_lang: originalLang }),
        });
    }

    /**
     * Get WebSocket base URL from VITE_BACKEND_URL or window.location
     * Converts http(s) to ws(s) protocol
     */
    private getWebSocketBaseUrl(): string {
        const backendUrl = import.meta.env.VITE_BACKEND_URL;
        if (backendUrl) {
            // Use VITE_BACKEND_URL if configured (production with separate API domain)
            const url = new URL(backendUrl, window.location.origin);
            const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${wsProtocol}//${url.host}`;
        } else {
            // Fallback to current host (development with Vite proxy)
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${wsProtocol}//${window.location.host}`;
        }
    }

    getChatWebSocketUrl(streamKey: string): string {
        const wsBaseUrl = this.getWebSocketBaseUrl();
        return `${wsBaseUrl}/api/chat/ws/${streamKey}`;
    }

    getAccessTokenForWebSocket(): string | null {
        return this.getAccessToken();
    }

    async listStreamViewers(streamKey: string): Promise<StreamViewerListResponse> {
        return this.request(`/api/streams/${streamKey}/viewers`);
    }

    async sendViewerHeartbeat(streamKey: string, status?: 'before_entry' | 'party' | 'peek' | 'two_shot'): Promise<void> {
        // Use /viewer-status endpoint which properly updates is_in_party and is_peeking flags
        await this.request(`/api/streams/${streamKey}/viewer-status`, {
            method: 'POST',
            body: JSON.stringify({ status: status || 'before_entry' }),
        });
    }

    async clearViewerHeartbeat(streamKey: string, options?: { keepalive?: boolean }): Promise<void> {
        await this.request(`/api/streams/${streamKey}/viewers/heartbeat`, {
            method: 'DELETE',
            keepalive: options?.keepalive ?? false,
        });
    }

    // Viewer points endpoints

    async getPointBalance(): Promise<PointBalanceResponse> {
        return this.request('/api/viewers/points');
    }

    async purchasePoints(amount: 1000 | 3000 | 5000 | 10000 | 30000 | 50000): Promise<PointBalanceResponse> {
        return this.request('/api/viewers/points/purchase', {
            method: 'POST',
            body: JSON.stringify({ amount }),
        });
    }

    async consumePointsForWatch(streamKey: string, status: 'waiting' | 'party' | 'peek' = 'waiting'): Promise<PointConsumptionResponse> {
        return this.request('/api/viewers/points/consume/watch', {
            method: 'POST',
            body: JSON.stringify({ stream_key: streamKey, status }),
        });
    }

    // Viewer profile endpoints

    async getMyProfile(): Promise<ViewerProfile> {
        return this.request('/api/viewers/me/profile');
    }

    async updateHandleName(handleName: string): Promise<HandleNameResponse> {
        return this.request('/api/viewers/me/handle-name', {
            method: 'PUT',
            body: JSON.stringify({ handle_name: handleName }),
        });
    }

    async getLanguage(): Promise<LanguageResponse> {
        return this.request('/api/viewers/me/language');
    }

    async updateLanguage(language: string): Promise<LanguageResponse> {
        return this.request('/api/viewers/me/language', {
            method: 'PUT',
            body: JSON.stringify({ language }),
        });
    }

    // Viewer account settings endpoints

    async viewerChangePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
        return this.request('/api/auth/viewer/change-password', {
            method: 'POST',
            body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
        });
    }

    async viewerChangeEmail(newEmail: string, password: string): Promise<{ message: string; email: string; requires_verification: boolean }> {
        return this.request('/api/auth/viewer/change-email', {
            method: 'POST',
            body: JSON.stringify({ new_email: newEmail, password }),
        });
    }

    async viewerWithdraw(currentPassword: string, pointsForfeitConfirmed: boolean): Promise<void> {
        await this.request('/api/viewers/me', {
            method: 'DELETE',
            body: JSON.stringify({
                current_password: currentPassword,
                points_forfeit_confirmed: pointsForfeitConfirmed,
            }),
        });
    }

    // Streamer profile endpoints

    async getStreamerProfile(username: string): Promise<import('./types').StreamerProfile> {
        return this.request(`/api/profile/${username}`);
    }

    async updateStreamerProfile(
        username: string,
        data: import('./types').StreamerProfileUpdate
    ): Promise<import('./types').StreamerProfile> {
        return this.request(`/api/profile/${username}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // Gallery endpoints

    async getStreamerGallery(username: string): Promise<import('./types').GalleryListResponse> {
        return this.request(`/api/gallery/${username}`);
    }

    async addGalleryImage(
        file: File,
        displayOrder: number
    ): Promise<{ id: number; streamer_id: number; display_order: number; status: 'pending'; preview_path: string }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('display_order', String(displayOrder));
        const headers: Record<string, string> = {};
        if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
        const response = await fetch(`${API_BASE_URL}/api/gallery`, {
            method: 'POST',
            headers,
            body: formData,
        });
        if (!response.ok) throw await this.toApiError(response);
        return response.json();
    }

    async deleteGalleryImage(imageId: number): Promise<void> {
        return this.request(`/api/gallery/${imageId}`, {
            method: 'DELETE',
        });
    }

    // Streamer message endpoints

    async sendMessage(
        streamerUsername: string,
        message: string
    ): Promise<import('./types').MessageSendResponse> {
        return this.request('/api/messages', {
            method: 'POST',
            body: JSON.stringify({ streamer_username: streamerUsername, message }),
        });
    }

    async getMessageInbox(): Promise<import('./types').StreamerMessage[]> {
        return this.request('/api/messages/inbox');
    }

    async markMessageAsRead(messageId: number): Promise<import('./types').StreamerMessage> {
        return this.request(`/api/messages/${messageId}/read`, {
            method: 'PUT',
        });
    }

    // Viewer memo endpoints

    async getViewerMemo(streamKey: string): Promise<import('./types').ViewerMemo> {
        return this.request(`/api/memos/${streamKey}`);
    }

    async saveViewerMemo(
        streamKey: string,
        memo: string
    ): Promise<import('./types').ViewerMemo> {
        return this.request(`/api/memos/${streamKey}`, {
            method: 'PUT',
            body: JSON.stringify({ memo }),
        });
    }

    // Party count endpoint

    async getPartyCount(streamKey: string): Promise<import('./types').PartyCountResponse> {
        return this.request(`/api/streams/${streamKey}/party-count`);
    }

    // Update viewer status
    async updateViewerStatus(streamKey: string, status: 'before_entry' | 'party' | 'peek' | 'two_shot'): Promise<void> {
        await this.request(`/api/streams/${streamKey}/viewer-status`, {
            method: 'POST',
            body: JSON.stringify({ status }),
        });
    }

    async getStreamerStatus(streamKey: string): Promise<{
        status: 'waiting' | 'party' | 'two_shot';
        party_count: number;
        two_shot_count: number;
    }> {
        return this.request(`/api/streams/${streamKey}/streamer-status`);
    }

    // Secret message endpoints

    async getSecretTemplates(): Promise<SecretMessageTemplate[]> {
        return this.request('/api/chat/secret/templates');
    }

    async sendSecretMessage(
        streamKey: string,
        message: string,
        templateId?: number,
        scheduleId?: string,
        originalLang?: string
    ): Promise<SecretMessage> {
        return this.request('/api/chat/secret/messages', {
            method: 'POST',
            body: JSON.stringify({
                stream_key: streamKey,
                message,
                template_id: templateId,
                schedule_id: scheduleId,
                original_lang: originalLang
            }),
        });
    }

    async getSecretMessages(streamKey: string, limit: number = 50, scheduleId?: string, lang?: string): Promise<SecretMessage[]> {
        const params = new URLSearchParams({ stream_key: streamKey, limit: limit.toString() });
        if (scheduleId !== undefined) {
            params.append('schedule_id', scheduleId);
        }
        if (lang !== undefined) {
            params.append('lang', lang);
        }
        return this.request(`/api/chat/secret/messages?${params.toString()}`);
    }

    // Profile image upload endpoints

    /**
     * Upload a profile image for staff review. The image becomes public only after approval,
     * so ``profile_image_url`` is the currently approved image (possibly null).
     */
    async uploadProfileImage(file: File): Promise<ProfileImageUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);

        const url = `${API_BASE_URL}/api/upload/profile-image`;
        const headers: Record<string, string> = {};

        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) throw await this.toApiError(response);

        return response.json();
    }

    async deleteProfileImage(): Promise<{ message: string }> {
        return this.request('/api/upload/profile-image', {
            method: 'DELETE',
        });
    }

    async uploadNotificationImage(file: File): Promise<{ message: string; image_url: string; warning?: string }> {
        const formData = new FormData();
        formData.append('file', file);

        const url = `${API_BASE_URL}/api/upload/notification-image`;
        const headers: Record<string, string> = {};

        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new ApiError(response.status, error.detail || 'Upload failed');
        }

        return response.json();
    }

    // Favorites endpoints

    async getFavorites(): Promise<FavoriteListResponse> {
        return this.request('/api/favorites/');
    }

    async checkFavoriteStatus(streamerId: number): Promise<FavoriteStatusResponse> {
        return this.request(`/api/favorites/${streamerId}/status`);
    }

    async addFavorite(streamerId: number): Promise<{ message: string }> {
        return this.request(`/api/favorites/${streamerId}`, {
            method: 'POST',
        });
    }

    async removeFavorite(streamerId: number): Promise<void> {
        return this.request(`/api/favorites/${streamerId}`, {
            method: 'DELETE',
        });
    }

    async getFavoriteSchedules(): Promise<FavoriteScheduleListResponse> {
        return this.request('/api/favorites/schedules');
    }

    // Event Notification Subscription endpoints

    async subscribeEventNotification(scheduleId: string): Promise<EventNotificationSubscription> {
        return this.request('/api/event-notifications/subscribe', {
            method: 'POST',
            body: JSON.stringify({ schedule_id: scheduleId }),
        });
    }

    async unsubscribeEventNotification(scheduleId: string): Promise<void> {
        return this.request(`/api/event-notifications/unsubscribe/${scheduleId}`, {
            method: 'DELETE',
        });
    }

    async checkEventNotificationStatus(scheduleId: string): Promise<EventNotificationStatus> {
        return this.request(`/api/event-notifications/check/${scheduleId}`);
    }

    async getMyEventNotificationSubscriptions(): Promise<EventNotificationWithEvent[]> {
        return this.request('/api/event-notifications/my-subscriptions');
    }

    // Site settings endpoints

    async getScheduleNoticePrefix(streamerId?: number): Promise<ScheduleNoticePrefix> {
        const url = streamerId
            ? `/api/site-settings/schedule-notice-prefix?streamer_id=${streamerId}`
            : '/api/site-settings/schedule-notice-prefix';
        return this.request(url);
    }

    async getCurrentScheduleNoticePrefix(): Promise<ScheduleNoticePrefix> {
        return this.request('/api/site-settings/schedule-notice-prefix/current');
    }

    async updateScheduleNoticePrefix(value: string): Promise<ScheduleNoticePrefix> {
        return this.request('/api/site-settings/schedule-notice-prefix', {
            method: 'PUT',
            body: JSON.stringify({ value }),
        });
    }

    // STT settings endpoints

    async getSTTSettings(): Promise<STTSettingsResponse> {
        return this.request('/api/site-settings/stt');
    }

    async updateSTTSettings(settings: STTSettingsUpdate): Promise<STTSettingsResponse> {
        return this.request('/api/site-settings/stt', {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    }

    // Translation settings endpoints

    async getTranslationSettings(): Promise<TranslationSettingsResponse> {
        return this.request('/api/site-settings/translation');
    }

    async updateTranslationSettings(settings: TranslationSettingsUpdate): Promise<TranslationSettingsResponse> {
        return this.request('/api/site-settings/translation', {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    }

    // Notification endpoints

    async getNotifications(limit: number = 50, offset: number = 0): Promise<NotificationListResponse> {
        return this.request(`/api/notifications/?limit=${limit}&offset=${offset}`);
    }

    async getUnreadNotificationCount(): Promise<{ unread_count: number }> {
        return this.request('/api/notifications/unread-count');
    }

    async markNotificationsAsRead(notificationIds: number[]): Promise<{ message: string }> {
        return this.request('/api/notifications/mark-read', {
            method: 'POST',
            body: JSON.stringify({ notification_ids: notificationIds }),
        });
    }

    async createNotification(data: NotificationCreate): Promise<Notification> {
        return this.request('/api/notifications/', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async deleteNotification(notificationId: number): Promise<void> {
        return this.request(`/api/notifications/${notificationId}`, {
            method: 'DELETE',
        });
    }

    async getSentNotifications(limit: number = 50, offset: number = 0): Promise<SentNotificationListResponse> {
        return this.request(`/api/notifications/sent?limit=${limit}&offset=${offset}`);
    }

    async sendBulkNotification(data: BulkNotificationCreate): Promise<BulkNotificationResponse> {
        return this.request('/api/notifications/bulk', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // ===========================================
    // eKYC Streamer Methods (配信者向け)
    // ===========================================

    /**
     * eKYC申請を作成（配信者のみ）
     * GMO顔認証eKYC: 外部本人確認画面を開始
     */
    async startEKYC(): Promise<EKYCApplicationResponse> {
        return this.request('/api/ekyc/start', {
            method: 'POST',
        });
    }

    /**
     * eKYC申請を作成（配信者のみ）
     * レガシー/ローカル用: 本人確認書類 + 顔写真をアップロード
     */
    async applyEKYC(idDocument: File, selfie: File): Promise<EKYCApplicationResponse> {
        const formData = new FormData();
        formData.append('id_document', idDocument);
        formData.append('selfie', selfie);

        const url = `${API_BASE_URL}/api/ekyc/apply`;
        const headers: Record<string, string> = {};

        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new ApiError(response.status, error.detail || 'eKYC申請に失敗しました');
        }

        return response.json();
    }

    /**
     * 自分のeKYC状態を取得（配信者のみ）
     */
    async getMyEKYCStatus(): Promise<EKYCStatusResponse> {
        return this.request('/api/ekyc/status');
    }

    // ===========================================
    // eKYC Admin Methods (管理者向け)
    // ===========================================

    /**
     * eKYC申請一覧を取得（管理者のみ）
     */
    async listEKYCApplications(
        page: number = 1,
        perPage: number = 20,
        statusFilter?: EKYCStatus
    ): Promise<EKYCApplicationListResponse> {
        const params = new URLSearchParams({
            page: page.toString(),
            per_page: perPage.toString(),
        });
        if (statusFilter) {
            params.append('status_filter', statusFilter);
        }
        return this.request(`/api/ekyc/admin/applications?${params.toString()}`);
    }

    /**
     * eKYC申請詳細を取得（管理者のみ）
     */
    async getEKYCApplication(applicationId: number): Promise<EKYCApplicationResponse> {
        return this.request(`/api/ekyc/admin/applications/${applicationId}`);
    }

    /**
     * eKYC申請画像を取得し、表示用の一時Object URLを返す（管理者のみ）
     */
    async getEKYCApplicationImageObjectUrl(
        applicationId: number,
        imageKind: 'id_document' | 'selfie'
    ): Promise<string> {
        if (this.isTokenExpiringSoon()) {
            await this.refresh();
        }

        const endpoint = `/api/ekyc/admin/applications/${applicationId}/images/${imageKind}`;
        const url = `${API_BASE_URL}${endpoint}`;
        const headers: Record<string, string> = {};
        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        let response = await fetch(url, {
            credentials: 'include',
            headers,
        });

        if (response.status === 401 && this.refreshToken) {
            const refreshed = await this.refresh();
            if (refreshed && this.accessToken) {
                headers['Authorization'] = `Bearer ${this.accessToken}`;
                response = await fetch(url, {
                    credentials: 'include',
                    headers,
                });
            }
        }

        if (!response.ok) {
            let errorMessage = '画像の取得に失敗しました';
            try {
                const error = await response.json();
                if (typeof error.detail === 'string') {
                    errorMessage = error.detail;
                }
            } catch {
                // File endpoints may not return JSON errors through every proxy.
            }
            throw new ApiError(response.status, errorMessage);
        }

        return URL.createObjectURL(await response.blob());
    }

    /**
     * eKYC申請を審査（管理者のみ）
     */
    async reviewEKYCApplication(
        applicationId: number,
        data: EKYCReviewRequest
    ): Promise<EKYCReviewResponse> {
        return this.request(`/api/ekyc/admin/applications/${applicationId}/review`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // ===========================================
    // Banner Methods (バナー管理)
    // ===========================================

    /**
     * アクティブなバナー一覧を取得（公開API）
     */
    async getBanners(): Promise<BannerListResponse> {
        return this.request('/api/banners/');
    }

    /**
     * 全バナー一覧を取得（管理者のみ）
     */
    async getAllBanners(): Promise<BannerListResponse> {
        return this.request('/api/banners/all');
    }

    /**
     * 特定のバナーを取得
     */
    async getBanner(location: string): Promise<Banner> {
        return this.request(`/api/banners/${location}`);
    }

    /**
     * バナー設定を更新（管理者のみ）
     */
    async updateBanner(location: string, data: BannerUpdateRequest): Promise<Banner> {
        return this.request(`/api/banners/${location}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    /**
     * バナー画像をアップロード（管理者のみ）
     */
    async uploadBannerImage(location: string, file: File): Promise<BannerUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);

        const url = `${API_BASE_URL}/api/banners/${location}/upload`;
        const headers: Record<string, string> = {};

        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new ApiError(response.status, error.detail || 'バナー画像のアップロードに失敗しました');
        }

        return response.json();
    }

    // ==================== Backup API ====================

    async createContentReport(data: {
        subject_type: 'live_room' | 'profile'; streamer_id: string;
        stream_key?: string | null; reason: ReportReason; details: string;
    }): Promise<ContentReport> {
        return this.request<ContentReport>('/api/reports/', { method: 'POST', body: JSON.stringify(data) });
    }

    async listContentReports(
        status?: ReportStatus, limit: number = 20, offset: number = 0, filters: Omit<ContentReportFilters, 'status'> = {},
    ): Promise<ContentReportListResponse> {
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (status) params.set('status', status);
        if (filters.openOnly) params.set('open_only', 'true');
        if (filters.overdueOnly) params.set('overdue_only', 'true');
        if (filters.channel) params.set('channel', filters.channel);
        const query = `?${params.toString()}`;
        return this.request<ContentReportListResponse>(`/api/reports/${query}`);
    }

    /**
     * @param acquirerReportedAt ISO8601 with timezone to record, null to clear, undefined to keep.
     */
    async updateContentReport(
        id: number, status: ReportStatus, resolution_note: string, acquirerReportedAt?: string | null,
    ): Promise<ContentReport> {
        const body: Record<string, unknown> = { status, resolution_note };
        if (acquirerReportedAt !== undefined) body.acquirer_reported_at = acquirerReportedAt;
        return this.request<ContentReport>(`/api/reports/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    }

    async createExternalComplaint(data: ExternalComplaintCreate): Promise<ContentReport> {
        return this.request<ContentReport>('/api/reports/external', { method: 'POST', body: JSON.stringify(data) });
    }

    // ==================== Moderation API ====================

    async listModerationLiveStreams(): Promise<LiveStreamSummary[]> {
        return this.request<LiveStreamSummary[]>('/api/moderation/live-streams');
    }

    async forceStopStream(streamKey: string, data: ModerationReasonRequest): Promise<ForceStopResponse> {
        return this.request<ForceStopResponse>(`/api/moderation/streams/${encodeURIComponent(streamKey)}/force-stop`, {
            method: 'POST', body: JSON.stringify(data),
        });
    }

    async liftPublishBlock(streamerId: number, reason: string): Promise<StreamerModerationState> {
        return this.request<StreamerModerationState>(`/api/moderation/streamers/${streamerId}/publish-block/lift`, {
            method: 'POST', body: JSON.stringify({ reason }),
        });
    }

    async listRestrictedStreamers(): Promise<StreamerModerationState[]> {
        return this.request<StreamerModerationState[]>('/api/moderation/restricted-streamers');
    }

    async suspendStreamer(streamerId: number, reason: string): Promise<SuspensionResponse> {
        return this.request<SuspensionResponse>(`/api/moderation/streamers/${streamerId}/suspend`, {
            method: 'POST', body: JSON.stringify({ reason }),
        });
    }

    async liftStreamerSuspension(streamerId: number, reason: string): Promise<StreamerModerationState> {
        return this.request<StreamerModerationState>(`/api/moderation/streamers/${streamerId}/suspension/lift`, {
            method: 'POST', body: JSON.stringify({ reason }),
        });
    }

    async getMyModerationStatus(): Promise<MyModerationStatus> {
        return this.request<MyModerationStatus>('/api/moderation/me');
    }

    async getStreamerContent(streamerId: number): Promise<StreamerContent> {
        return this.request<StreamerContent>(`/api/moderation/streamers/${streamerId}/content`);
    }

    async listNgWords(): Promise<NgWord[]> {
        return this.request<NgWord[]>('/api/moderation/ng-words');
    }

    async createNgWord(word: string, action: NgWordAction): Promise<NgWord> {
        return this.request<NgWord>('/api/moderation/ng-words', { method: 'POST', body: JSON.stringify({ word, action }) });
    }

    async updateNgWord(id: number, action: NgWordAction): Promise<NgWord> {
        return this.request<NgWord>(`/api/moderation/ng-words/${id}`, { method: 'PATCH', body: JSON.stringify({ action }) });
    }

    async deleteNgWord(id: number): Promise<void> {
        await this.request(`/api/moderation/ng-words/${id}`, { method: 'DELETE' });
    }

    async listNgDetections(limit: number = 20, offset: number = 0, outcome?: 'rejected' | 'flagged'): Promise<NgDetectionListResponse> {
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (outcome) params.set('outcome', outcome);
        return this.request<NgDetectionListResponse>(`/api/moderation/ng-detections?${params.toString()}`);
    }

    async listPendingMedia(): Promise<MediaReviewItem[]> {
        return this.request<MediaReviewItem[]>('/api/moderation/media/pending');
    }

    async approveMedia(kind: MediaKind, itemId: number): Promise<MediaReviewItem> {
        return this.request<MediaReviewItem>(`/api/moderation/media/${kind}/${itemId}/approve`, { method: 'POST' });
    }

    async rejectMedia(kind: MediaKind, itemId: number, data: ModerationReasonRequest): Promise<void> {
        await this.request(`/api/moderation/media/${kind}/${itemId}/reject`, { method: 'POST', body: JSON.stringify(data) });
    }

    async takedownGalleryImage(imageId: number, data: ModerationReasonRequest): Promise<void> {
        await this.request(`/api/moderation/gallery/${imageId}/takedown`, { method: 'POST', body: JSON.stringify(data) });
    }

    async takedownProfileImage(streamerId: number, data: ModerationReasonRequest): Promise<void> {
        await this.request(`/api/moderation/streamers/${streamerId}/profile-image/takedown`, {
            method: 'POST', body: JSON.stringify(data),
        });
    }

    async takedownProfileField(streamerId: number, field: string, data: ModerationReasonRequest): Promise<void> {
        await this.request(`/api/moderation/streamers/${streamerId}/profile-fields/${encodeURIComponent(field)}/takedown`, {
            method: 'POST', body: JSON.stringify(data),
        });
    }

    async takedownChatMessage(messageId: number, data: ModerationReasonRequest): Promise<void> {
        await this.request(`/api/moderation/chat-messages/${messageId}/takedown`, { method: 'POST', body: JSON.stringify(data) });
    }

    async listModerationActions(
        options: { actionType?: string; month?: string; limit?: number; offset?: number } = {},
    ): Promise<ModerationActionListResponse> {
        const params = new URLSearchParams({ limit: String(options.limit ?? 50), offset: String(options.offset ?? 0) });
        if (options.actionType) params.set('action_type', options.actionType);
        if (options.month) params.set('month', options.month);
        return this.request<ModerationActionListResponse>(`/api/moderation/actions?${params.toString()}`);
    }

    /** Fetch an authenticated binary resource, refreshing the access token once on 401. */
    private async fetchAuthorizedBlob(endpoint: string, fallbackMessage: string): Promise<Blob> {
        if (this.isTokenExpiringSoon()) {
            await this.refresh();
        }
        const url = `${API_BASE_URL}${endpoint}`;
        const headers: Record<string, string> = {};
        if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
        let response = await fetch(url, { credentials: 'include', headers });
        if (response.status === 401 && this.refreshToken) {
            const refreshed = await this.refresh();
            if (refreshed && this.accessToken) {
                headers['Authorization'] = `Bearer ${this.accessToken}`;
                response = await fetch(url, { credentials: 'include', headers });
            }
        }
        if (!response.ok) {
            let message = fallbackMessage;
            try {
                const error = await response.json();
                if (typeof error.detail === 'string') message = error.detail;
            } catch {
                // Binary endpoints may not return JSON errors through every proxy.
            }
            throw new ApiError(response.status, message);
        }
        return response.blob();
    }

    /** Pending profile images are private; load them as a temporary object URL for staff or the owner. */
    async getModerationMediaObjectUrl(previewPath: string): Promise<string> {
        return URL.createObjectURL(await this.fetchAuthorizedBlob(previewPath, '画像の取得に失敗しました'));
    }

    async downloadMonthlyReport(kind: MonthlyReportKind, month: string): Promise<void> {
        const blob = await this.fetchAuthorizedBlob(
            `/api/moderation/monthly-report/${kind}.csv?month=${encodeURIComponent(month)}`,
            '月次レポートのダウンロードに失敗しました',
        );
        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = kind === 'actions' ? `moderation-actions-${month}.csv` : `complaints-${month}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(downloadUrl);
    }

    async listBackups(page: number = 1, pageSize: number = 10): Promise<BackupListResponse> {
        const params = new URLSearchParams({
            page: page.toString(),
            page_size: pageSize.toString(),
        });
        return this.request<BackupListResponse>(`/api/backups?${params}`);
    }

    async createBackup(): Promise<BackupCreateResponse> {
        return this.request<BackupCreateResponse>('/api/backups', {
            method: 'POST',
        });
    }

    async downloadBackup(filename: string): Promise<void> {
        const url = `${API_BASE_URL}/api/backups/${encodeURIComponent(filename)}/download`;
        const headers: Record<string, string> = {};

        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        const response = await fetch(url, { headers });

        if (!response.ok) {
            const error = await response.json();
            throw new ApiError(response.status, error.detail || 'ダウンロードに失敗しました');
        }

        // Trigger file download
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
    }

    async deleteBackup(filename: string): Promise<BackupDeleteResponse> {
        return this.request<BackupDeleteResponse>(`/api/backups/${encodeURIComponent(filename)}`, {
            method: 'DELETE',
        });
    }

    async verifyBackup(filename: string, testRestore: boolean = false): Promise<BackupVerifyResponse> {
        const params = new URLSearchParams();
        if (testRestore) {
            params.append('test_restore', 'true');
        }
        const queryString = params.toString();
        const endpoint = `/api/backups/${encodeURIComponent(filename)}/verify${queryString ? `?${queryString}` : ''}`;
        return this.request<BackupVerifyResponse>(endpoint, {
            method: 'POST',
        });
    }
}

// Export singleton instance
export const apiClient = new AuthApiClient();
export { ApiError, SessionExpiredError };
